import Image from "@tiptap/extension-image";
import type { ImageOptions } from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import { Plugin, PluginKey } from "prosemirror-state";
import ResizableImageView from "./ResizableImageView.vue";

export interface ResizableImageOptions extends ImageOptions {
  enableImagePaste: boolean;
}

/**
 * Handles image upload with immediate feedback and background processing
 *
 * Process:
 * 1. Show base64 image immediately for instant visual feedback
 * 2. Upload file to server in background
 * 3. Replace base64 image with uploaded URL once complete
 * 4. Handle upload errors gracefully (keeps base64 on failure)
 *
 * @param file - Image file to upload
 * @param editor - TipTap editor instance (for accessing storage)
 * @param view - ProseMirror view instance (for DOM manipulation)
 * @param pos - Optional position to insert image (defaults to cursor position)
 */
const handleImageUpload = (file: File, editor: any, view: any, pos?: number) => {
  try {
    // Get upload store from editor dependency injection
    const uploadStore = editor.storage.uploadDocumentStore;

    if (uploadStore && uploadStore.uploadFileToS3fromTemplateEditor) {
      // Phase 1: Show base64 version immediately for user feedback
      const reader = new FileReader();
      reader.onload = async (readerEvent) => {
        const tempImageUrl = readerEvent.target?.result as string;

        // Create temporary image node with base64 data
        const tempNode = view.state.schema.nodes.image.create({
          src: tempImageUrl,
          width: "300px",
          align: "center",
        });

        // Insert temporary image at specified position or cursor location
        const insertPos = pos ?? view.state.selection.head;
        let transaction = view.state.tr.insert(insertPos, tempNode);
        view.dispatch(transaction);

        try {
          // Phase 2: Upload file to server in background
          const uploadedUrl = await uploadStore.uploadFileToS3fromTemplateEditor(file);

          if (uploadedUrl) {
            // Register URL mapping to prevent base64 URL from being marked as "deleted"
            // This tells the Editor component that tempImageUrl -> uploadedUrl
            if (editor.storage.mapImageUrl) {
              editor.storage.mapImageUrl(tempImageUrl, uploadedUrl);
            }

            // Phase 3: Replace temporary base64 image with final uploaded URL
            const doc = view.state.doc;
            let imagePos = -1;

            // Find the temporary image in the document
            doc.descendants((node: any, pos: number) => {
              if (node.type.name === "image" && node.attrs.src === tempImageUrl) {
                imagePos = pos;
                return false; // Stop searching once found
              }
            });

            // Replace the temporary image with the final uploaded version
            if (imagePos !== -1) {
              const updatedNode = view.state.schema.nodes.image.create({
                src: uploadedUrl, // Use final uploaded URL
                width: tempNode.attrs.width, // Preserve original width
                align: tempNode.attrs.align, // Preserve original alignment
              });

              // Create transaction to replace the node
              transaction = view.state.tr.replaceWith(imagePos, imagePos + tempNode.nodeSize, updatedNode);
              view.dispatch(transaction);
            }
          }
        } catch (uploadError) {
          console.error("Upload error:", uploadError);
          // Graceful degradation: base64 image remains visible to user
          // They can still see their image even if upload failed
        }
      };
      // Convert file to base64 and trigger the upload process
      reader.readAsDataURL(file);
    } else {
      // Fallback: No upload service available, just show base64 image
      // This ensures images still work even without server upload capability
      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        const imageUrl = readerEvent.target?.result as string;
        const node = view.state.schema.nodes.image.create({
          src: imageUrl, // Base64 data URL
          width: "300px",
          align: "center",
        });

        const insertPos = pos ?? view.state.selection.head;
        const transaction = view.state.tr.insert(insertPos, node);
        view.dispatch(transaction);
      };
      reader.readAsDataURL(file);
    }
  } catch (error) {
    // Log any unexpected errors during image processing
    console.error("Image processing error:", error);
  }
};

// Convert a base64 data URL to a File
const base64ToFile = (dataUrl: string): File => {
  const [metadata, base64Data] = dataUrl.split(",");
  const mimeType = metadata.match(/:(.*?);/)?.[1] || "image/png";
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const extension = mimeType.split("/")[1] || "png";
  return new File([bytes], `pasted_image_${Date.now()}.${extension}`, { type: mimeType });
};

// Upload a base64 image already inserted in the doc (via default HTML paste from DOCX, etc.)
// and replace every occurrence of its src with the final S3 URL so saved content stays small.
const uploadAndReplaceBase64InDoc = async (dataUrl: string, editor: any, view: any) => {
  const uploadStore = editor.storage.uploadDocumentStore;
  if (!uploadStore?.uploadFileToS3fromTemplateEditor) return;

  try {
    const file = base64ToFile(dataUrl);
    const uploadedUrl = await uploadStore.uploadFileToS3fromTemplateEditor(file);
    if (!uploadedUrl) return;

    if (editor.storage.mapImageUrl) {
      editor.storage.mapImageUrl(dataUrl, uploadedUrl);
    }

    const positions: Array<{ pos: number; nodeSize: number; attrs: any }> = [];
    view.state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === "image" && node.attrs.src === dataUrl) {
        positions.push({ pos, nodeSize: node.nodeSize, attrs: node.attrs });
      }
    });
    if (positions.length === 0) return;

    let tr = view.state.tr;
    // Replace from the end to keep earlier positions valid
    for (const { pos, nodeSize, attrs } of positions.reverse()) {
      const updatedNode = view.state.schema.nodes.image.create({
        src: uploadedUrl,
        width: attrs.width,
        align: attrs.align,
      });
      tr = tr.replaceWith(pos, pos + nodeSize, updatedNode);
    }
    view.dispatch(tr);
  } catch (error) {
    console.error("Base64 image upload error:", error);
  }
};

/**
 * Enhanced Image extension with copy-paste support, resizing, and upload capabilities
 *
 * Features:
 * - Copy-paste images from clipboard
 * - Drag & drop image files
 * - Immediate base64 preview with background upload
 * - Image resizing and alignment
 * - Conditional activation based on editor configuration
 */
export const ResizableImage = Image.extend<ResizableImageOptions>({
  name: "image",

  /**
   * Extension options configuration
   */
  addOptions() {
    return {
      ...this.parent?.(),
      enableImagePaste: true, // Whether to enable copy-paste functionality
    };
  },

  /**
   * Define additional attributes for image nodes
   */
  addAttributes() {
    return {
      ...this.parent?.(),
      // Custom width attribute for image resizing
      width: {
        default: "300px",
        parseHTML: (element) => element.getAttribute("width") || "300px",
        renderHTML: (attributes) => ({
          width: attributes.width,
        }),
      },
      // Custom alignment attribute for image positioning
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
          // Apply CSS margin based on alignment value
          style: `display:block; margin:${
            attributes.align === "left"
              ? "0 auto 0 0" // Left: margin-right auto
              : attributes.align === "right"
                ? "0 0 0 auto" // Right: margin-left auto
                : "0 auto" // Center: margin left/right auto
          };`,
        }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return VueNodeViewRenderer(ResizableImageView);
  },

  /**
   * Add ProseMirror plugins for image handling
   * Only adds plugins if image paste functionality is enabled
   */
  addProseMirrorPlugins() {
    const editor = this.editor;

    // Don't add copy-paste plugins if image functionality is disabled
    // This prevents any image operations when images are not allowed
    if (!this.options.enableImagePaste) {
      return [];
    }

    return [
      new Plugin({
        key: new PluginKey("imageClipboard"),
        props: {
          /**
           * Handle paste events from clipboard
           * Intercepts image data and processes it for upload
           */
          handlePaste(view, event, slice) {
            const items = Array.from(event.clipboardData?.items || []);

            // Check each clipboard item for images
            for (const item of items) {
              // Handle images copied from clipboard (screenshots, copied images, etc.)
              if (item.type.indexOf("image") === 0) {
                event.preventDefault(); // Prevent default paste behavior

                const file = item.getAsFile();
                if (file) {
                  // Process the image file through our upload handler
                  handleImageUpload(file, editor, view);
                }
                return true; // Indicate we handled this paste event
              }
            }

            // External HTML paste (e.g. from DOCX) embeds images as inline base64.
            // Let the default paste land the content, then upload each base64 image
            // and swap its src for the S3 URL so saved content stays small.
            const html = event.clipboardData?.getData("text/html");
            if (html?.includes("data:image/")) {
              const dom = new DOMParser().parseFromString(html, "text/html");
              const dataUrls = Array.from(dom.querySelectorAll('img[src^="data:image/"]'))
                .map((img) => img.getAttribute("src"))
                .filter((src): src is string => !!src);
              const uniqueDataUrls = Array.from(new Set(dataUrls));
              if (uniqueDataUrls.length > 0) {
                // Defer until after ProseMirror has inserted the pasted nodes
                setTimeout(() => {
                  uniqueDataUrls.forEach((dataUrl) => uploadAndReplaceBase64InDoc(dataUrl, editor, view));
                }, 0);
              }
            }

            return false; // Let other handlers process non-image paste events
          },

          /**
           * Handle drag & drop events for images
           * Processes dropped image files and uploads them
           */
          handleDrop(view, event, slice, moved) {
            // Only handle external drops (not internal editor content moves)
            if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
              // Filter for image files only
              const files = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith("image/"));

              // If no image files, let other handlers process the drop
              if (files.length === 0) return false;

              event.preventDefault(); // Prevent default drop behavior

              // Calculate drop position based on mouse coordinates
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });

              // If we can't determine position, abort
              if (!coordinates) return false;

              // Process each dropped image file
              files.forEach((file) => {
                handleImageUpload(file, editor, view, coordinates.pos);
              });

              return true; // Indicate we handled this drop event
            }
            return false; // Let other handlers process non-image drops
          },
        },
      }),
    ];
  },
});
